// utils/formatDate.js
import { format } from "date-fns";

/**
 * Get user's date format preference from localStorage
 * @returns {string} Date format string
 */
export const getUserDateFormat = () => {
  try {
    const appSetting = JSON.parse(localStorage.getItem('appSettings') || '{}');
    return appSetting.dateFormat || 'DD/MM/YYYY'; // Default fallback
  } catch (error) {
    console.error('Error reading date format from localStorage:', error);
    return 'DD/MM/YYYY'; // Default fallback
  }
};

/**
 * Get user's time format preference from localStorage
 * @returns {string} Time format string
 */
export const getUserTimeFormat = () => {
  try {
    const appSetting = JSON.parse(localStorage.getItem('appSettings') || '{}');
    return appSetting.timeFormat || '12hr'; // Default fallback
  } catch (error) {
    console.error('Error reading time format from localStorage:', error);
    return '12hr'; // Default fallback
  }
};

/**
 * Convert user date format to date-fns format string
 * @param {string} userFormat - User's preferred date format (DD/MM/YYYY, MM/DD/YYYY, YYYY/MM/DD)
 * @returns {string} date-fns compatible format string
 */
export const convertToDateFnsFormat = (userFormat) => {
  const formatMap = {
    'DD/MM/YYYY': 'dd/MM/yyyy',
    'MM/DD/YYYY': 'MM/dd/yyyy',
    'YYYY/MM/DD': 'yyyy/MM/dd',
    'DD-MM-YYYY': 'dd-MM-yyyy',
    'MM-DD-YYYY': 'MM-dd-yyyy',
    'YYYY-MM-DD': 'yyyy-MM-dd',
    'DD.MM.YYYY': 'dd.MM.yyyy',
    'MM.DD.YYYY': 'MM.dd.yyyy',
    'YYYY.MM.DD': 'yyyy.MM.dd',
    'YYYY.MMM.DD': 'yyyy.mmm.dd',
  };

  return formatMap[userFormat] || 'dd/MM/yyyy'; // Default fallback
};

/**
 * Get time format string based on user preference
 * @param {string} timeFormat - User's preferred time format (12hr or 24hr)
 * @returns {string} date-fns compatible time format string
 */
export const getTimeFormatString = (timeFormat) => {
  return timeFormat === '24hr' ? 'HH:mm' : 'hh:mm a';
};

/**
 * Format date according to user's preferences
 * @param {Date|string} date - Date to format
 * @param {boolean} includeTime - Whether to include time in the format
 * @param {boolean} longFormat - Whether to use long format (with day name, etc.)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, includeTime = false, longFormat = false) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    const userDateFormat = getUserDateFormat();
    const userTimeFormat = getUserTimeFormat();

    const dateFnsFormat = convertToDateFnsFormat(userDateFormat);
    const timeFormatString = getTimeFormatString(userTimeFormat);

    if (longFormat) {
      // Long format: "Monday, 25/12/2023 at 2:30 PM"
      const dayFormat = 'EEEE';
      const atText = includeTime ? ' at ' : '';
      const timeFormat = includeTime ? timeFormatString : '';
      return format(dateObj, `${dayFormat}, ${dateFnsFormat}${atText}${timeFormat}`);
    } else if (includeTime) {
      // Short format with time: "25/12/2023 2:30 PM"
      return format(dateObj, `${dateFnsFormat} ${timeFormatString}`);
    } else {
      // Date only: "25/12/2023"
      return format(dateObj, dateFnsFormat);
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format date for display in tables/lists (compact format)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateCompact = (date) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    const userDateFormat = getUserDateFormat();
    const userTimeFormat = getUserTimeFormat();

    // Use shorter month format for compact display
    let compactFormat;
    switch (userDateFormat) {
      case 'DD/MM/YYYY':
        compactFormat = 'dd MMM yyyy';
        break;
      case 'MM/DD/YYYY':
        compactFormat = 'MMM dd, yyyy';
        break;
      case 'YYYY/MM/DD':
        compactFormat = 'yyyy MMM dd';
        break;
      default:
        compactFormat = 'dd MMM yyyy';
    }

    const timeFormatString = getTimeFormatString(userTimeFormat);
    return format(dateObj, `${compactFormat} ${timeFormatString}`);
  } catch (error) {
    console.error('Error formatting compact date:', error);
    return '';
  }
};

/**
 * Format date for date picker display button
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForPicker = (date) => {
  if (!date) return 'Select date';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Select date';

    const userDateFormat = getUserDateFormat();

    // Always use abbreviated month format for date picker
    let pickerFormat;
    switch (userDateFormat) {
      case 'DD/MM/YYYY':
        pickerFormat = 'dd MMM yyyy';
        break;
      case 'MM/DD/YYYY':
        pickerFormat = 'MMM dd, yyyy';
        break;
      case 'YYYY/MM/DD':
        pickerFormat = 'yyyy MMM dd';
        break;
      default:
        pickerFormat = 'dd MMM yyyy';
    }

    return format(dateObj, pickerFormat);
  } catch (error) {
    console.error('Error formatting date for picker:', error);
    return 'Select date';
  }
};

/**
 * Format time only according to user's preference
 * @param {Date|string} date - Date to extract time from
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    const userTimeFormat = getUserTimeFormat();
    const timeFormatString = getTimeFormatString(userTimeFormat);

    return format(dateObj, timeFormatString);
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
};

// Export all functions for easy importing
export default {
  formatDate,
  formatDateCompact,
  formatDateForPicker,
  formatTime,
  getUserDateFormat,
  getUserTimeFormat,
  convertToDateFnsFormat,
  getTimeFormatString
};